import { Box, Typography } from "@mui/material";
import React from "react";
import SEO from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const BALMA_HOURS = [
  { day: "Lundi", h: "10h–14h & 15h–20h" },
  { day: "Mardi", h: "10h–14h" },
  { day: "Mercredi", h: "10h–14h & 15h–20h" },
  { day: "Jeudi", h: "14h–20h" },
  { day: "Samedi", h: "14h–17h" },
];

const CAST_HOURS = [
  { day: "Lundi", h: "10h–13h & 14h–19h" },
  { day: "Mardi", h: "10h–13h & 14h–19h" },
  { day: "Mercredi", h: "10h–13h & 14h–19h" },
  { day: "Jeudi", h: "10h–13h & 14h–19h" },
  { day: "Vendredi", h: "10h–13h & 14h–19h" },
  { day: "Samedi", h: "14h–17h" },
];

const sectionLabelSx = {
  fontSize: ".65rem",
  fontWeight: 600,
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: C.textMuted,
  mb: ".5rem",
};

const HoursRow = ({ rows }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
    {rows.map((r) => (
      <Box
        key={r.day}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: ".8rem",
          color: C.text,
        }}
      >
        <Box component="span" sx={{ color: C.textMuted }}>
          {r.day}
        </Box>
        <Box component="span">{r.h}</Box>
      </Box>
    ))}
  </Box>
);

const Cabinets = () => (
  <>
    <SEO
      title="Cabinets & urgences ostéopathiques  Balma & Castelnau"
      description="Maxime Nicolaieff reçoit à Balma (31130) et Castelnau-d'Estrétefonds (31620). Horaires, accès, visites à domicile et urgences ostéopathiques sous 24h  disponible jours fériés et week-end."
      path="/cabinets"
    />
    <Box component="section" sx={SX.pageHeroSection}>
      <Box sx={SX.pageHeroInner}>
        <Box component="span" sx={SX.sectionLabel}>
          Cabinets
        </Box>
        <Typography variant="h1" component="h1" sx={SX.displayTitle}>
          Deux adresses, <em>une même approche.</em>
        </Typography>
        <Typography sx={SX.pageHeroSub}>
          Maxime Nicolaieff vous reçoit dans deux cabinets en région
          toulousaine. Consultations sur rendez-vous, du lundi au samedi.
        </Typography>
      </Box>
    </Box>

    {/* ── Cards ───────────────────────────────── */}
    <Box
      sx={{
        maxWidth: maxW,
        mx: "auto",
        px: { xs: "1rem", md: "2.5rem" },
        pb: { xs: "4rem", md: "5rem" },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: "1.5rem",
      }}
    >
      {[
        {
          num: "Cabinet 1",
          name: "As Cambiots Balma",
          addr: "8 Route de Mons, 31130 Balma",
          mapSrc:
            "https://maps.google.com/maps?q=8+Route+de+Mons+31130+Balma&output=embed",
          mapTitle: "Cabinet Balma",
          hours: BALMA_HOURS,
          acces: "Bus lignes 84 & 72 Rez-de-chaussée · Parking gratuit",
          payment: "Chèques, espèces, carte bancaire, virement",
          rdvHref: "https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff",
        },
        {
          num: "Cabinet 2",
          name: "Maison Aphéléia Castelnau-d'Estrétefonds",
          addr: "164 Allée du Camp del Rey, 31620 Castelnau-d'Estrétefonds",
          phone: "06 51 08 46 97",
          phoneTel: "0651084697",
          mapSrc:
            "https://maps.google.com/maps?q=164+All%C3%A9e+du+Camp+del+Rey+31620+Castelnau-d%27Estr%C3%A9tefonds&output=embed",
          mapTitle: "Cabinet Castelnau",
          hours: CAST_HOURS,
          acces: "Bus lignes 077 & 002 Entrée accessible · Parking gratuit",
          payment: (
            <span>
              Chèques, espèces, virement <em>(cartes non acceptées)</em>
            </span>
          ),
          rdvHref:
            "https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff?pid=practice-482733",
        },
      ].map((cab) => (
        <Box
          key={cab.num}
          sx={{
            background: C.surface,
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.sm,
            border: `1px solid ${C.border}`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "220px",
              background: C.bgAlt,
              "& iframe": {
                width: "100%",
                height: "100%",
                border: 0,
                display: "block",
              },
            }}
          >
            <iframe
              title={cab.mapTitle}
              src={cab.mapSrc}
              allowFullScreen=""
              loading="lazy"
            />
          </Box>
          <Box
            sx={{
              padding: "1.75rem",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: ".65rem",
                fontWeight: 600,
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: C.accent,
                mb: ".4rem",
              }}
            >
              {cab.num}
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontFamily: F.display,
                fontSize: "1.4rem",
                fontWeight: 400,
                color: C.text,
                mb: ".25rem",
              }}
            >
              {cab.name}
            </Typography>
            <Typography
              sx={{
                fontSize: ".82rem",
                color: C.textMuted,
                mb: cab.phone ? ".4rem" : "1.5rem",
              }}
            >
              {cab.addr}
            </Typography>
            {cab.phone && (
              <Box
                component="a"
                href={`tel:${cab.phoneTel}`}
                sx={{
                  fontSize: ".82rem",
                  color: C.primary,
                  textDecoration: "none",
                  mb: "1.5rem",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {cab.phone}
              </Box>
            )}

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
            >
              <Box>
                <Typography sx={sectionLabelSx}>Horaires</Typography>
                <HoursRow rows={cab.hours} />
              </Box>
              <Box>
                <Typography sx={sectionLabelSx}>Accès</Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.6,
                  }}
                >
                  {cab.acces}
                </Typography>
              </Box>
              <Box>
                <Typography sx={sectionLabelSx}>Paiement</Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.6,
                  }}
                >
                  {cab.payment}
                </Typography>
              </Box>
            </Box>

            <Box
              component="a"
              href={cab.rdvHref}
              target="_blank"
              rel="noreferrer"
              sx={{
                display: "block",
                mt: "auto",
                pt: "1.75rem",
                py: ".7rem",
                px: "1.5rem",
                background: C.dark,
                color: "#fff",
                borderRadius: "50px",
                fontSize: ".82rem",
                fontWeight: 500,
                textDecoration: "none",
                textAlign: "center",
                transition: `all ${trans}`,
                "&:hover": { background: C.primary },
              }}
            >
              Prendre rendez-vous
            </Box>
          </Box>
        </Box>
      ))}
    </Box>

    {/* ── Urgences ostéopathiques ───────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5rem" }, background: C.bgAlt }}
    >
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Urgences
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2rem" }}
        >
          Une douleur aiguë ? <em>Je réponds sous 24h.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: "1.25rem",
            mb: "2.5rem",
          }}
        >
          {[
            {
              icon: "📞",
              title: "Joignable",
              desc: "Jours fériés et week-end  laissez un message ou un SMS si je suis en consultation.",
            },
            {
              icon: "⏱️",
              title: "Délai 24h max",
              desc: "Pour les urgences ostéopathiques, je m'engage à vous rappeler et vous recevoir sous 24 heures.",
            },
            {
              icon: "📍",
              title: "Zone couverte",
              desc: "Nord et Nord-Est de Toulouse. Déplacement à domicile dans un rayon de 20 km.",
            },
            {
              icon: "🏃",
              title: "Traumatismes sportifs",
              desc: "Entorses, chocs, blocages articulaires  les blessures de sport sont prises en charge en urgence.",
            },
          ].map((item) => (
            <Box
              key={item.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
              }}
            >
              <Box
                component="span"
                sx={{ fontSize: "1.5rem", display: "block", mb: ".75rem" }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: ".88rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".4rem",
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: ".78rem",
                  color: C.textMuted,
                  lineHeight: 1.65,
                }}
              >
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <Box component="a" href="tel:0695788983" sx={SX.btnDark}>
            06 95 78 89 83 Balma
          </Box>
          <Box component="a" href="tel:0651084697" sx={SX.btnOutline}>
            06 51 08 46 97 Castelnau
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── Visites à domicile ─────────────────── */}
    <Box
      component="section"
      id="contact"
      sx={{ background: C.dark, pt: "5.5rem", pb: "2.5rem" }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabelWhite}>
            À domicile
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: "1rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Quand vous ne pouvez pas vous déplacer,
            <br />
            <em>je viens à vous.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".88rem",
              color: "rgba(255,255,255,.55)",
              maxWidth: "420px",
              lineHeight: 1.75,
              mb: "1.75rem",
            }}
          >
            Je me déplace directement chez vous sur Balma et ses environs dans
            un rayon de 20 km. Idéal pour les personnes à mobilité réduite ou en
            maternité. Tarif domicile : 70 € (+ 10 € de déplacement).
          </Typography>
          <Box component="a" href="tel:0695788983" sx={SX.btnOutlineWhite}>
            06 95 78 89 83
          </Box>
        </Box>

        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "5rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            20
          </Typography>
          <Typography
            sx={{
              fontSize: ".72rem",
              color: "rgba(255,255,255,.4)",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              mt: ".25rem",
            }}
          >
            km autour
            <br />
            du cabinet
          </Typography>
        </Box>
      </Box>
    </Box>
  </>
);

export default Cabinets;
